import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Grid,
  Alert,
  Divider,
  Chip,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { userAPI } from '../../services/api';

const CreateUser = () => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    new_user_type: '',
    wallet_limit: '',
    initial_balance: '',
  });
  const [loading, setLoading] = useState(false);
  const [creatorBalance, setCreatorBalance] = useState(null);

  // Define hierarchy
  const hierarchy = ['owner', 'super_admin', 'admin', 'agent', 'users'];

  useEffect(() => {
    // Initialize new_user_type based on current user's type
    const creatorLevel = hierarchy.indexOf(user?.user_type);
    if (creatorLevel !== -1 && creatorLevel < hierarchy.length - 1) {
      // Set to the next level in hierarchy
      setFormData(prev => ({
        ...prev,
        new_user_type: hierarchy[creatorLevel + 1]
      }));
    }
    fetchCreatorBalance();
  }, []);

  const fetchCreatorBalance = async () => {
    try {
      const response = await userAPI.getMyWallet();
      if (response.data.success) {
        const balance = parseFloat(response.data.wallet?.balance) || 0;
        setCreatorBalance(balance);
      }
    } catch (error) {
      console.error('Error fetching wallet:', error);
      setCreatorBalance(0);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.new_user_type) {
      toast.error('Please select a user type');
      return;
    }

    const initialBalance = parseFloat(formData.initial_balance) || 0;
    if (initialBalance > 0 && user?.user_type !== 'owner' && creatorBalance && creatorBalance < initialBalance) {
      toast.error(`Insufficient balance. You have ₹${(creatorBalance || 0).toFixed(2)}, but need ₹${initialBalance.toFixed(2)}`);
      return;
    }

    setLoading(true);

    try {
      const response = await userAPI.createUser({
        username: formData.username,
        password: formData.password,
        new_user_type: formData.new_user_type,
        wallet_limit: formData.wallet_limit ? parseFloat(formData.wallet_limit) : null,
        initial_balance: initialBalance,
      });
      
      toast.success(`${formData.new_user_type.charAt(0).toUpperCase() + formData.new_user_type.slice(1)} created successfully!`);
      
      // Reset form
      const creatorLevel = hierarchy.indexOf(user?.user_type);
      setFormData({
        username: '',
        password: '',
        new_user_type: creatorLevel < hierarchy.length - 1 ? hierarchy[creatorLevel + 1] : '',
        wallet_limit: '',
        initial_balance: '',
      });
      
      // Refresh creator balance
      fetchCreatorBalance();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  const getUserTypeOptions = () => {
    const creatorLevel = hierarchy.indexOf(user?.user_type);
    const options = [];

    // Creator can only create users at lower hierarchy levels
    if (creatorLevel !== -1) {
      for (let i = creatorLevel + 1; i < hierarchy.length; i++) {
        const userType = hierarchy[i];
        options.push({
          value: userType,
          label: userType.charAt(0).toUpperCase() + userType.slice(1)
        });
      }
    }

    return options;
  };

  const getGuidelineText = () => {
    const userType = user?.user_type;
    const guidelines = {
      owner: [
        'You can create super_admins, admins, agents, and users',
        'You have unlimited balance to assign',
        'Set wallet limits to control spending (except for super_admin)',
      ],
      super_admin: [
        'You can create admins, agents, and users',
        'Your balance can be used for initial transfers',
        'Admins cannot exceed your authorization level',
      ],
      admin: [
        'You can create agents and users',
        'Initial balance will be deducted from your wallet',
        'Agents and users can only receive transfers from you',
      ],
      agent: [
        'You can only create users',
        'Initial balance will be deducted from your wallet',
        'Users work under your supervision',
      ],
    };

    return guidelines[userType] || [];
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
          Create New User
        </Typography>
        <Box display="flex" gap={2} alignItems="center">
          <Typography variant="body2" color="text.secondary">
            Current User: <strong>{user?.username}</strong>
          </Typography>
          <Chip 
            label={user?.user_type?.toUpperCase()} 
            color="primary" 
            size="small"
          />
          {creatorBalance !== null && user?.user_type !== 'owner' && (
            <Chip 
              label={`Balance: ₹${(typeof creatorBalance === 'number' ? creatorBalance : 0).toFixed(2)}`}
              color="success"
              size="small"
            />
          )}
        </Box>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                User Information
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <form onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  label="Username"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  margin="normal"
                  required
                  disabled={loading}
                  placeholder="Enter unique username"
                />

                <TextField
                  fullWidth
                  label="Password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  margin="normal"
                  required
                  disabled={loading}
                  placeholder="Strong password recommended"
                />

                <FormControl fullWidth margin="normal" required>
                  <InputLabel>User Type</InputLabel>
                  <Select
                    name="new_user_type"
                    value={formData.new_user_type}
                    onChange={handleChange}
                    label="User Type"
                    disabled={loading}
                  >
                    {getUserTypeOptions().map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {formData.new_user_type && formData.new_user_type !== 'owner' && formData.new_user_type !== 'super_admin' && (
                  <TextField
                    fullWidth
                    label="Wallet Limit"
                    name="wallet_limit"
                    type="number"
                    value={formData.wallet_limit}
                    onChange={handleChange}
                    margin="normal"
                    disabled={loading}
                    helperText="Leave empty for no limit (maximum spending limit)"
                    inputProps={{ step: 0.01, min: 0 }}
                  />
                )}

                {user?.user_type !== 'owner' && (
                  <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
                    <Typography variant="body2">
                      <strong>Note:</strong> Any initial balance will be deducted from your wallet (₹{(typeof creatorBalance === 'number' ? creatorBalance : 0).toFixed(2)})
                    </Typography>
                  </Alert>
                )}

                <TextField
                  fullWidth
                  label="Initial Balance"
                  name="initial_balance"
                  type="number"
                  value={formData.initial_balance}
                  onChange={handleChange}
                  margin="normal"
                  disabled={loading}
                  helperText={user?.user_type === 'owner' ? 'Initial amount to assign to the new user' : 'Initial amount to transfer from your wallet'}
                  inputProps={{ step: 0.01, min: 0 }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading}
                  sx={{ mt: 3 }}
                >
                  {loading ? <CircularProgress size={24} /> : `Create ${formData.new_user_type ? formData.new_user_type.charAt(0).toUpperCase() + formData.new_user_type.slice(1) : 'User'}`}
                </Button>
              </form>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Create User Guidelines
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box component="ul" sx={{ pl: 2, mb: 2 }}>
                {getGuidelineText().map((guideline, index) => (
                  <li key={index}>
                    <Typography variant="body2">{guideline}</Typography>
                  </li>
                ))}
                <li>
                  <Typography variant="body2">Usernames must be unique and cannot be changed</Typography>
                </li>
                <li>
                  <Typography variant="body2">Wallet limits can be updated after creation</Typography>
                </li>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                User Hierarchy:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                {hierarchy.map((level, index) => (
                  <Chip
                    key={level}
                    label={level.charAt(0).toUpperCase() + level.slice(1)}
                    variant={user?.user_type === level ? 'filled' : 'outlined'}
                    color={user?.user_type === level ? 'primary' : 'default'}
                    size="small"
                  />
                ))}
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                ↓ Higher level can create lower level ↓
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </motion.div>
  );
};

export default CreateUser;
